import { useState, type FormEvent } from 'react';
import { Knop } from '@qc/ui';
import { supabase, verlopenLinkBijStart } from '../supabase.js';
import { Veld } from './Veld.js';

export interface InlogSchermProps {
  onKlaar: () => void;
}

type Modus = 'inloggen' | 'nieuw' | 'vergeten';

/**
 * Inloggen, aanmelden en wachtwoord vergeten op één scherm. De organisator
 * doet dit allemaal zelf, zonder dat wij iets voor hem hoeven te doen.
 */
export function InlogScherm({ onKlaar }: InlogSchermProps) {
  const [modus, setModus] = useState<Modus>('inloggen');
  const nieuw = modus === 'nieuw';
  const [email, setEmail] = useState('');
  const [wachtwoord, setWachtwoord] = useState('');
  const [fout, setFout] = useState<string | null>(
    verlopenLinkBijStart
      ? 'De link uit de mail is verlopen of al gebruikt. Vraag hieronder een nieuwe aan.'
      : null,
  );
  const [bezig, setBezig] = useState(false);
  /** Het adres waar de bevestigingsmail heen is; null zolang dat niet speelt. */
  const [bevestigMail, setBevestigMail] = useState<string | null>(null);
  /** Het adres waar de mail met de herstellink heen is. */
  const [herstelMail, setHerstelMail] = useState<string | null>(null);

  function wissel(naar: Modus) {
    setModus(naar);
    setFout(null);
  }

  async function verzend(gebeurtenis: FormEvent) {
    gebeurtenis.preventDefault();
    setFout(null);

    if (modus === 'vergeten') {
      setBezig(true);
      // Zonder redirectTo brengt de link naar SITE_URL, het dashboard zelf.
      const { error } = await supabase.auth.resetPasswordForEmail(email);
      setBezig(false);
      if (error) {
        setFout('Dat lukte niet. Probeer het over een minuut nog eens.');
        return;
      }
      setHerstelMail(email);
      return;
    }

    if (nieuw && wachtwoord.length < 8) {
      setFout('Kies een wachtwoord van minstens acht tekens.');
      return;
    }

    setBezig(true);
    const { data, error } = nieuw
      ? await supabase.auth.signUp({ email, password: wachtwoord })
      : await supabase.auth.signInWithPassword({ email, password: wachtwoord });
    setBezig(false);

    if (error) {
      setFout(
        nieuw
          ? 'Aanmelden lukte niet. Bestaat er misschien al een account met dit e-mailadres?'
          : 'Inloggen lukte niet. Controleer je e-mailadres en wachtwoord.',
      );
      return;
    }
    // Staat e-mailbevestiging aan, dan is er na aanmelden nog geen sessie:
    // zonder deze melding val je stil terug op het inlogscherm.
    if (nieuw && data.session === null) {
      setBevestigMail(email);
      return;
    }
    onKlaar();
  }

  if (herstelMail !== null) {
    return (
      <div className="mx-auto flex min-h-full max-w-lg flex-col justify-center gap-6 p-8">
        <header>
          <p className="text-tekst-zacht">Quality Cashless</p>
          <h1 className="text-3xl font-bold">Kijk in je mail</h1>
        </header>
        <p className="text-xl">
          Als er een account is voor <strong>{herstelMail}</strong>, hebben we daar een mail heen
          gestuurd. Klik op de link in die mail; daarna kies je een nieuw wachtwoord.
        </p>
        <p className="text-tekst-zacht">
          Geen mail gezien? Kijk even bij je ongewenste mail. Hij komt van info@qualitycashless.com.
        </p>
        <Knop
          nadruk="gewoon"
          onClick={() => {
            setHerstelMail(null);
            wissel('inloggen');
          }}
          className="w-full"
        >
          Naar inloggen
        </Knop>
      </div>
    );
  }

  if (bevestigMail !== null) {
    return (
      <div className="mx-auto flex min-h-full max-w-lg flex-col justify-center gap-6 p-8">
        <header>
          <p className="text-tekst-zacht">Quality Cashless</p>
          <h1 className="text-3xl font-bold">Bijna klaar</h1>
        </header>
        <p className="text-xl">
          We hebben een mail gestuurd naar <strong>{bevestigMail}</strong>. Klik op de link in die
          mail om je account te bevestigen; daarna ben je ingelogd.
        </p>
        <p className="text-tekst-zacht">
          Geen mail gezien? Kijk even bij je ongewenste mail. Hij komt van info@qualitycashless.com.
        </p>
        <Knop
          nadruk="gewoon"
          onClick={() => {
            setBevestigMail(null);
            wissel('inloggen');
          }}
          className="w-full"
        >
          Naar inloggen
        </Knop>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-full max-w-lg flex-col justify-center gap-6 p-8">
      <header>
        <p className="text-tekst-zacht">Quality Cashless</p>
        <h1 className="text-3xl font-bold">
          {modus === 'nieuw'
            ? 'Account aanmaken'
            : modus === 'vergeten'
              ? 'Wachtwoord vergeten'
              : 'Inloggen'}
        </h1>
        {modus === 'vergeten' && (
          <p className="mt-2 text-xl text-tekst-zacht">
            Vul je e-mailadres in. Je krijgt een mail met een link om een nieuw wachtwoord te
            kiezen.
          </p>
        )}
      </header>

      <form onSubmit={(e) => void verzend(e)} className="flex flex-col gap-4">
        <Veld
          label="E-mailadres"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {modus !== 'vergeten' && (
          <Veld
            label="Wachtwoord"
            type="password"
            autoComplete={nieuw ? 'new-password' : 'current-password'}
            required
            value={wachtwoord}
            onChange={(e) => setWachtwoord(e.target.value)}
            toelichting={nieuw ? 'Minstens acht tekens.' : null}
          />
        )}

        {fout !== null && <p className="text-lg text-fout">{fout}</p>}

        <Knop nadruk="hoofd" type="submit" disabled={bezig} className="w-full">
          {bezig
            ? 'Bezig…'
            : modus === 'nieuw'
              ? 'Account aanmaken'
              : modus === 'vergeten'
                ? 'Stuur mij een link'
                : 'Inloggen'}
        </Knop>
      </form>

      {modus === 'inloggen' && (
        <Knop nadruk="stil" onClick={() => wissel('vergeten')} className="text-base">
          Wachtwoord vergeten?
        </Knop>
      )}
      <Knop
        nadruk="stil"
        onClick={() => wissel(modus === 'inloggen' ? 'nieuw' : 'inloggen')}
        className="text-base"
      >
        {modus === 'inloggen' ? 'Ik ben nieuw en wil me aanmelden' : 'Ik heb al een account'}
      </Knop>
    </div>
  );
}
